import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import {
  type Purpose,
  CoreVariantOption,
  ExtraVariantOption,
} from "./theme.types";

const buttonVariants = cva(
  cn(
    "inline-flex items-center justify-center border shrink-0 transition-[color,box-shadow] overflow-hidden",
    "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
    "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
    "[&>svg]:size-3 gap-x-2 [&>svg]:pointer-events-none",
    "disabled:opacity-50 disabled:pointer-events-none",
  ),
  {
    variants: {
      variant: {
        [CoreVariantOption.Default]: "border-0",
        [CoreVariantOption.Secondary]: "border border-transparent",
        [CoreVariantOption.Outline]: "border-border text-foreground ",
        [ExtraVariantOption.Ghost]: "border-0",
        [ExtraVariantOption.Soft]:
          "text-primary hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        [ExtraVariantOption.White]:
          "text-primary hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        [ExtraVariantOption.Text]: "bg-transparent shadow-none border-0",
      },

      /**
       * @see {PurposeOption} in theme.types
       */
      purpose: {
        /**
         * @description @see{PurposeOption.Info} in theme.types
         */
        info: "",

        /**
         * Other purposes are defined in compoundVariants below
         */
        success: "",

        /**
         * @see {PurposeOption.Danger} in theme.types
         */
        danger: "",

        /**
         * @see {PurposeOption.Warning} in theme.types
         */
        warning: "",

        /**
         * @see {PurposeOption.Dark} in theme.types
         */
        dark: "",

        /**
         * @see {PurposeOption.Gray} in theme.types
         */
        gray: "",

        /**
         * @see {PurposeOption.Light} in theme.types
         */
        light: "",

        /**
         * @see {PurposeOption.Primary} in theme.types
         */
        primary: "",

        /**
         * @see {PurposeOption.Secondary} in theme.types
         */
        secondary: "",
      } satisfies Record<Purpose, string>,

      size: {
        sm: "py-2.5 px-3 has-[>svg]:px-2.5",
        input: "py-2 px-3 has-[>svg]:px-2.5 text-[15px]",
        default: "py-3 px-4 has-[>svg]:px-3 [&>svg]:size-3",
        lg: "py-5.5 px-5 sm:px-5 has-[>svg]:px-4 [&>svg]:size-4",
        xl: "p-4 sm:px-5 has-[>svg]:px-4 [&>svg]:size-5",
        "2xl": "p-4 sm:px-5 has-[>svg]:px-4 [&>svg]:size-6",
        icon: "size-7 p-0 [&>svg]:size-7",
        iconLg: "size-9.5 p-0 [&>svg]:size-4",
      },
      shape: {
        default: "rounded-lg",
        pill: "rounded-full",
        rounded: "rounded-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      purpose: "gray",
      size: "default",
      shape: "default",
    },
    compoundVariants: [
      {
        variant: "default",
        purpose: "info",
        className:
          "bg-blue-600 text-white [a&]:hover:bg-blue-700 hover:bg-blue-700 focus:bg-blue-700 ",
      },
      {
        variant: "secondary",
        purpose: "info",
        className:
          "bg-blue-100 text-blue-800 hover:bg-blue-200  focus:bg-blue-200 ",
      },
      {
        variant: "outline",
        purpose: "info",
        className:
          "border-blue-600 text-blue-600 hover:border-blue-500 hover:text-blue-500 focus:border-blue-500 focus:text-blue-500 ",
      },
      {
        variant: "default",
        purpose: "primary",
        className:
          "bg-primary text-white  [a&]:hover:bg-primary-hover hover:bg-primary-hover focus:bg-primary-hover ",
      },
      {
        variant: "secondary",
        purpose: "primary",
        className:
          "bg-primary-100 text-primary-800 hover:bg-primary-200  focus:bg-primary-200 ",
      },
      {
        variant: "outline",
        purpose: "primary",
        className:
          "border-primary text-primary hover:text-primary/80 hover:bg-primary/10",
      },
      {
        variant: "default",
        purpose: "secondary",
        className:
          "bg-secondary text-white [a&]:hover:bg-secondary-hover hover:bg-secondary-hover focus:bg-secondary-hover ",
      },
      {
        variant: "secondary",
        purpose: "secondary",
        className:
          "bg-secondary-100 text-secondary-800 hover:bg-secondary-200  focus:bg-secondary-200 ",
      },
      {
        variant: "outline",
        purpose: "secondary",
        className: "border-secondary text-secondary",
      },
      {
        variant: "default",
        purpose: "success",
        className:
          "bg-teal-500 text-white [a&]:hover:bg-teal-600 hover:bg-teal-600 focus:bg-teal-600 ",
      },
      {
        variant: "secondary",
        purpose: "success",
        className:
          "bg-teal-100 text-teal-800 hover:bg-teal-200  focus:bg-teal-200 ",
      },
      {
        variant: "outline",
        purpose: "success",
        className:
          "border-teal-500 text-teal-500 hover:border-teal-400 hover:text-teal-400 focus:border-teal-400 focus:text-teal-400 ",
      },
      {
        variant: "default",
        purpose: "danger",
        className:
          "bg-red-500 text-white [a&]:hover:bg-red-600 hover:bg-red-600 focus:bg-red-600 ",
      },
      {
        variant: "secondary",
        purpose: "danger",
        className:
          "bg-red-100 text-red-800 hover:bg-red-200  focus:bg-red-200 ",
      },
      {
        variant: "outline",
        purpose: "danger",
        className:
          "border-red-500 text-red-500 hover:border-red-400 hover:text-red-400 focus:border-red-400 focus:text-red-400 ",
      },
      {
        variant: "default",
        purpose: "warning",
        className:
          "bg-yellow-500 text-white [a&]:hover:bg-yellow-600 hover:bg-yellow-600 focus:bg-yellow-600 ",
      },
      {
        variant: "secondary",
        purpose: "warning",
        className:
          "bg-yellow-100 text-yellow-800 hover:bg-yellow-200  focus:bg-yellow-200 ",
      },
      {
        variant: "outline",
        purpose: "warning",
        className:
          "border-yellow-500 text-yellow-500 hover:border-yellow-400 hover:text-yellow-400 focus:border-yellow-400 focus:text-yellow-400 ",
      },
      {
        variant: "default",
        purpose: "dark",
        className:
          "bg-gray-800 text-white [a&]:hover:bg-gray-900 hover:bg-gray-900 focus:bg-gray-900 ",
      },
      {
        variant: "default",
        purpose: "gray",
        className:
          "bg-gray-500 text-white [a&]:hover:bg-gray-300 hover:bg-gray-600 focus:bg-gray-600 ",
      },
      {
        variant: "default",
        purpose: "light",
        className:
          "bg-white text-gray-600 [a&]:hover:bg-gray-200 hover:bg-gray-200 focus:bg-gray-200 ",
      },
      {
        variant: "secondary",
        purpose: "dark",
        className:
          "bg-gray-100 text-gray-800 hover:bg-gray-300  focus:bg-gray-300 ",
      },
      {
        variant: "secondary",
        purpose: "gray",
        className:
          "bg-gray-100 text-gray-500 hover:bg-gray-200  focus:bg-gray-200 ",
      },
      {
        variant: "secondary",
        purpose: "light",
        className:
          "bg-white/10 text-white hover:bg-white/20 focus:bg-white/20 ",
      },
      {
        variant: "outline",
        purpose: "dark",
        className:
          "border-gray-800 text-gray-800 hover:border-gray-500 hover:text-gray-500 focus:border-gray-500 focus:text-gray-500 ",
      },
      {
        variant: "outline",
        purpose: "gray",
        className:
          "border-gray-500 text-gray-500 hover:border-gray-800 hover:text-gray-800 focus:border-gray-800 focus:text-gray-800 ",
      },
      {
        variant: "ghost",
        purpose: "dark",
        className:
          "text-gray-800 hover:bg-gray-100 hover:text-gray-500 dark:hover:bg-accent/50 ",
      },
      {
        variant: "ghost",
        purpose: "info",
        className: "text-blue-600 hover:bg-blue-50 ",
      },
      {
        variant: "white",
        purpose: "dark",
        className: "border-gray-50 text-neutral-800 hover:bg-gray-200 ",
      },
      {
        variant: "ghost",
        purpose: "danger",
        className: "text-red-500 hover:bg-red-50 hover:text-red-600",
      },
      {
        variant: "ghost",
        purpose: "primary",
        className:
          "text-primary hover:bg-primary/10 hover:text-primary [&[href]]:hover:underline [&[href]]:underline-offset-4",
      },
      {
        variant: "text",
        purpose: "dark",
        className:
          "text-center font-sans text-base font-semibold leading-normal text-gray-800 hover:text-gray-500 dark:hover:text-gray-500",
      },
      {
        variant: "ghost",
        purpose: "gray",
        className:
          "bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-600",
      },
      {
        variant: "text",
        purpose: "info",
        className: "text-blue-600 hover:text-blue-700 focus:ring-blue-300",
      },
      {
        variant: "text",
        purpose: "primary",
        className: "text-primary hover:text-primary/80",
      },
      {
        variant: "text",
        purpose: "secondary",
        className: "text-secondary hover:text-secondary/80",
      },
      {
        variant: "text",
        purpose: "success",
        className: "text-teal-500 hover:text-teal-600",
      },
      {
        variant: "text",
        purpose: "danger",
        className: "text-red-500 hover:text-red-600",
      },
      {
        variant: "text",
        purpose: "warning",
        className: "text-yellow-500 hover:text-yellow-600",
      },
      {
        variant: "text",
        purpose: "dark",
        className: "text-gray-800 hover:text-gray-900",
      },
      {
        variant: "text",
        purpose: "gray",
        className: "text-gray-500 hover:text-gray-600",
      },
      {
        variant: "text",
        purpose: "light",
        className: "text-white hover:text-gray-200",
      },
    ],
  },
);

function Button({
  className,
  variant,
  purpose,
  size,
  shape,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-purpose={purpose}
      data-size={size}
      data-shape={shape}
      className={cn(
        buttonVariants({ variant, purpose, size, shape }),
        className,
      )}
      {...props}
    />
  );
}

Button.displayName = "Button";

export { Button, buttonVariants };
